import React, { useState, useEffect } from "react";
import { getCurrentUser, getUser, partialUpdateUser } from "../../services/UserService"

const CustomVocab = ({ languagesArray, languageOptions, userData }) => {
  const [input, setInput] = useState("");
  const [tags, setTags] = useState([]);
  const [uniqueTags, setUniqueTags] = useState([]);
  const [existingTags, setExistingTags] = useState([]);
  const [language, setLanguage] = useState(
    userData?.defaultLanguage || "english"
  );

  const fetchExistingTags = async () => {
    const existingCustomVocabulary = userData.custom_vocabulary || {};
    // Fetch the custom vocabulary for the selected language
    const selectedLanguageVocabulary = existingCustomVocabulary[language] || [];
    setExistingTags(selectedLanguageVocabulary);
  };

  useEffect(() => {
    fetchExistingTags();
  }, [language]);

  const createTags = (input) => {
    setTags(
      input
        .split(",")
        .filter((tag) => tag.trim() !== "")
        .map((tag) => tag.trim())
    );
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    createTags(e.target.value);
  };

  const handleDeleteTag = async (tagToDelete) => {
    let user = getCurrentUser();
    getUser(user.uid,
        async(response) => {
          const userData = response.data;
          const existingCustomVocabulary = userData.custom_vocabulary || {};
          const selectedLanguageVocabulary =
            existingCustomVocabulary[language] || [];

          // Filter out the tag to delete from the selected language's vocabulary
          const updatedLanguageVocabulary = selectedLanguageVocabulary.filter(
            (tag) => tag !== tagToDelete
          );

          // Update the Firestore document only for the selected language
          existingCustomVocabulary[language] = updatedLanguageVocabulary;
          await partialUpdateUser(user.uid, { custom_vocabulary: existingCustomVocabulary })
          console.log("Tag deleted from Firestore");
          setExistingTags(updatedLanguageVocabulary);
        }
    )
  };

  const fetchDataAndUpdateFirestore = async () => {
    let user = getCurrentUser();
    getUser(user.uid,
        async(response) => {
          const userData = response.data;
          const existingCustomVocabulary = userData.custom_vocabulary || {};
          const selectedLanguageVocabulary =
            existingCustomVocabulary[language] || [];

          // Check if tags are from the same language before combining
          const combinedTagsSet = new Set([
            ...selectedLanguageVocabulary,
            ...uniqueTags.filter(
              (tag) => !selectedLanguageVocabulary.includes(tag)
            ),
          ]);

          const custom_vocabulary = Array.from(combinedTagsSet);

          // Update custom vocabulary only for the selected language
          existingCustomVocabulary[language] = custom_vocabulary;

          // Update the entire custom vocabulary object with uniqueTags
          await partialUpdateUser(user.uid, { custom_vocabulary: existingCustomVocabulary })
          console.log("custom_vocabulary added to Firestore");
          fetchExistingTags();
        }
    )
  };

  useEffect(() => {
    // Clear uniqueTags when the language changes
    setUniqueTags([]);
  }, [language]);

  useEffect(() => {
    fetchDataAndUpdateFirestore();
  }, [uniqueTags, language]);

  const addCustomVocab = async () => {
    console.log("addCustomVocab is called");
    setUniqueTags((prevUniqueTags) => {
      const unique_custom_vocabulary = Array.from(
        new Set([...tags, ...prevUniqueTags])
      );
      return unique_custom_vocabulary;
    });
  };

  const handleKeyDown = async (e) => {
    if (e.key === "Enter") {
      e.preventDefault();

      await addCustomVocab();
      setInput("");
      setTags([]);
    }
  };

  return (
    <div className="container">
      <div className="drop-shadow-lg mx-auto bg-white rounded-md px-[15px] py-[5px] my-[10px]">
        <div className="text-[#07457F] 3xl:text-[20px] text-[16px] font-semibold">
          Select the Language and Enter custom vocabulary words or phrases
          divided by a comma (',').
          <br />
          <div className="my-[10px] flex justify-between items-center gap-[20px] flex-wrap">
            <div className="relative transition">
              <select
                value={language}
                onChange={(e) => {
                  setLanguage(e.target.value);
                }}
                className="cursor-pointer block appearance-none w-28 sm:w-32 transition bg-white border border-[#07457f] text-[#07457f] py-1 px-2 pr-6 rounded leading-tight text-sm sm:text-base focus:outline-none focus:bg-white focus:border-gray-500"
              >
                {Object.entries(languageOptions).map(([value, optionText]) => {
                  if(value != "english"){
                    return false;
                  }
                  return (
                    <option
                      key={value}
                      value={value}
                      disabled={!languagesArray.includes(value)}
                    >
                      {optionText}
                    </option>
                  )
                })}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-[#07457f]">
                <svg className="fill-current h-4 w-4" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 8l4 4 4-4H6z" />
                </svg>
              </div>
            </div>
          </div>
          Press Enter when you're done.
        </div>

        <textarea
          className="py-[10px] w-full h-auto outline-none"
          placeholder="Enter word/phrases here..."
          id="textarea"
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
        />
      </div>

      <div id="tags" className="flex flex-wrap gap-0">
        {tags.map((tag, index) => (
          <span
            key={index}
            className="tag m-[5px] bg-[#07457F] text-white rounded-full px-2 py-2 inline-block shadow-md cursor-not-allowed"
          >
            {tag}
          </span>
        ))}
        {existingTags.map((tag, index) => (
          <div key={index} className="relative">
            <span className="tag bg-[#07457F] m-2 text-white rounded-full px-2 py-2 inline-block shadow-md cursor-not-allowed">
              {tag}
            </span>
            <button
              className="delete-button bg-white text-black drop-shadow-md font-bold rounded-full px-2 py-1 text-xs cursor-pointer absolute top-0 right-0 -mt-1 -mr-1"
              onClick={() => handleDeleteTag(tag)}
            >
              X
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CustomVocab;
